import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getStoredSession } from '@/lib/auth-client';
import { isAggregateScope } from '@/lib/session-scope';
import { loadSelectedBrandIds, saveSelectedBrandIds } from '@/lib/brand-filter-storage';
import { flattenScopedLocations, type ScopedLocation } from '@/lib/scoped-locations';
import type { AggregateSessionScope, ScopedBrand, SessionScope } from '@/types/auth';

export interface BrandFilterValue {
  /** True only for a parent-master-client session spanning several brands. */
  isAggregate: boolean;
  rootMasterClientId: number | null;
  /** Every brand the session can see, sorted by name. Empty for line sessions. */
  brands: ScopedBrand[];
  brandById: Map<string, ScopedBrand>;
  /** The operator's selection, or `null` for "all brands in scope". */
  selectedBrandIds: string[] | null;
  selectedBrands: ScopedBrand[];
  /** Exactly one brand is in play, so brand-targeted writes have an unambiguous target. */
  isSingleBrand: boolean;
  activeBrand: ScopedBrand | null;
  /**
   * Whether a brand-targeted action (kill switch, coupon creation) has a target:
   * always true for line sessions, only under a single-brand filter in aggregate.
   */
  hasBrandTarget: boolean;
  /** `brandIds` to send with list requests — undefined means "everything in scope". */
  brandIdsParam: string[] | undefined;
  /** Stable fragment for TanStack Query keys, so narrowing the filter refetches. */
  brandFilterKey: string;
  /** Branches of the selected brands, brand-labeled. Empty for line sessions. */
  scopedLocations: ScopedLocation[];
  locationById: Map<string, ScopedLocation>;
  setSelectedBrandIds: (ids: string[] | null) => void;
  selectAll: () => void;
  selectOnly: (brandId: string) => void;
  /** Widen the selection to include one brand (deep link to an out-of-filter item). */
  includeBrand: (brandId: string) => void;
}

const LINE_SESSION_VALUE: BrandFilterValue = {
  isAggregate: false,
  rootMasterClientId: null,
  brands: [],
  brandById: new Map(),
  selectedBrandIds: null,
  selectedBrands: [],
  isSingleBrand: false,
  activeBrand: null,
  hasBrandTarget: true,
  brandIdsParam: undefined,
  brandFilterKey: 'line',
  scopedLocations: [],
  locationById: new Map(),
  setSelectedBrandIds: () => {},
  selectAll: () => {},
  selectOnly: () => {},
  includeBrand: () => {},
};

const BrandFilterContext = createContext<BrandFilterValue>(LINE_SESSION_VALUE);

/**
 * The header brand filter of an aggregate (parent master client) session.
 *
 * Line sessions get a fixed no-op value, so every consumer can call `useBrandFilter()`
 * unconditionally and just pass `brandIdsParam` / `brandFilterKey` through.
 */
export function BrandFilterProvider({ children }: { children: React.ReactNode }) {
  // getStoredSession() re-parses sessionStorage on every render, so the scope it hands back is a
  // fresh object each time. Re-derive the catalogue only when its CONTENT changes — otherwise the
  // memos and callbacks below get a new identity on every render, and the effects that depend on
  // them (the inbox fetch, the deep-link widening) would re-fire in a loop.
  const scopeJson = JSON.stringify(getStoredSession()?.scope ?? null);
  const aggregate = useMemo<AggregateSessionScope | null>(() => {
    const parsed = JSON.parse(scopeJson) as SessionScope | null;
    return isAggregateScope(parsed) ? parsed : null;
  }, [scopeJson]);

  const brands = useMemo(
    () => (aggregate ? [...aggregate.brands].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [aggregate],
  );
  const allBrandIds = useMemo(() => brands.map((brand) => brand.id), [brands]);
  const rootMasterClientId = aggregate?.rootMasterClientId ?? null;

  const [selectedBrandIds, setSelectedBrandIdsState] = useState<string[] | null>(() =>
    rootMasterClientId === null ? null : loadSelectedBrandIds(rootMasterClientId, allBrandIds),
  );

  const setSelectedBrandIds = useCallback(
    (ids: string[] | null) => {
      // An empty selection is meaningless (it would hide every row) — treat it as "all".
      const next = ids && ids.length > 0 && ids.length < allBrandIds.length ? ids : null;
      setSelectedBrandIdsState(next);
      if (rootMasterClientId !== null) saveSelectedBrandIds(rootMasterClientId, next, allBrandIds);
    },
    [allBrandIds, rootMasterClientId],
  );

  const selectAll = useCallback(() => setSelectedBrandIds(null), [setSelectedBrandIds]);
  const selectOnly = useCallback((brandId: string) => setSelectedBrandIds([brandId]), [setSelectedBrandIds]);
  const includeBrand = useCallback(
    (brandId: string) => {
      setSelectedBrandIdsState((current) => {
        if (current === null || current.includes(brandId)) return current;
        const next = [...current, brandId];
        const widened = next.length < allBrandIds.length ? next : null;
        if (rootMasterClientId !== null) saveSelectedBrandIds(rootMasterClientId, widened, allBrandIds);
        return widened;
      });
    },
    [allBrandIds, rootMasterClientId],
  );

  const value = useMemo<BrandFilterValue>(() => {
    if (!aggregate) return LINE_SESSION_VALUE;
    const selection = selectedBrandIds;
    const selectedBrands = selection ? brands.filter((brand) => selection.includes(brand.id)) : brands;
    const activeBrand = selectedBrands.length === 1 ? selectedBrands[0] : null;
    const scopedLocations = flattenScopedLocations(selectedBrands);
    return {
      isAggregate: true,
      rootMasterClientId: aggregate.rootMasterClientId,
      brands,
      brandById: new Map(brands.map((brand) => [brand.id, brand])),
      selectedBrandIds: selection,
      selectedBrands,
      isSingleBrand: activeBrand !== null,
      activeBrand,
      hasBrandTarget: activeBrand !== null,
      brandIdsParam: selection ?? undefined,
      brandFilterKey: selection ? [...selection].sort().join(',') : 'all',
      scopedLocations,
      locationById: new Map(scopedLocations.map((location) => [location.id, location])),
      setSelectedBrandIds,
      selectAll,
      selectOnly,
      includeBrand,
    };
  }, [aggregate, brands, selectedBrandIds, setSelectedBrandIds, selectAll, selectOnly, includeBrand]);

  return <BrandFilterContext.Provider value={value}>{children}</BrandFilterContext.Provider>;
}

export function useBrandFilter(): BrandFilterValue {
  return useContext(BrandFilterContext);
}
